import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IonContent, IonHeader, IonIcon, IonPage, IonToolbar } from "@ionic/react";
import { checkmarkCircleOutline, globeOutline, logoApple, logoFacebook, logoGooglePlaystore } from "ionicons/icons";
import bgFestival from "../images/bg_festival.jpg";
import "./CityUpdates.css";
import "./UpdatesPortal.css";

const FUNCTIONS_BASE = "https://us-central1-bacolod-ai-assistant.cloudfunctions.net";

type InviteInfo = {
  token: string;
  label: string;
  status: string;
  expiresAtMs: number | null;
};

type CandidatePage = {
  pageId: string;
  pageName: string;
  category: string | null;
  pictureUrl: string | null;
  approvalStatus?: "pending" | "approved" | null;
};

const apiUrl = (name: string) => `${FUNCTIONS_BASE}/${name}`;

const formatExpiry = (expiresAtMs: number | null) => {
  if (!expiresAtMs) return "No expiry";

  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(expiresAtMs));
};

const mergeCandidatePageStatuses = (
  pages: CandidatePage[],
  statuses: Array<{ pageId: string; approvalStatus?: "pending" | "approved" | null }>
) => {
  const statusesByPageId = new Map(statuses.map((status) => [status.pageId, status.approvalStatus || null]));

  return pages.map((page) => ({
    ...page,
    approvalStatus: statusesByPageId.get(page.pageId) ?? page.approvalStatus ?? null,
  }));
};

const UpdatesConnect: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const inviteToken = params.get("invite") || "";
  const oauthCode = params.get("code") || "";
  const oauthState = params.get("state") || "";
  const isOauthReturn = Boolean(oauthCode && oauthState);

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState("");
  const [isLoadingInvite, setIsLoadingInvite] = useState(true);
  const [isStartingAuth, setIsStartingAuth] = useState(false);
  const [isHandlingCallback, setIsHandlingCallback] = useState(false);
  const [submittingPageId, setSubmittingPageId] = useState<string | null>(null);
  const [partnerMessage, setPartnerMessage] = useState("");
  const [candidatePages, setCandidatePages] = useState<CandidatePage[]>([]);
  const [callbackState, setCallbackState] = useState("");
  const handledOauthRef = useRef(false);

  const clearOauthParams = useCallback(() => {
    const next = new URL(window.location.href);
    next.searchParams.delete("code");
    next.searchParams.delete("state");
    next.searchParams.delete("fb");
    window.history.replaceState({}, "", `${next.pathname}${next.search}`);
  }, []);

  const loadInvite = useCallback(async () => {
    if (!inviteToken) {
      if (isOauthReturn) {
        setIsLoadingInvite(false);
        return;
      }

      setInviteError("This invite link is missing a token.");
      setIsLoadingInvite(false);
      return;
    }

    setIsLoadingInvite(true);
    setInviteError("");

    try {
      const response = await fetch(`${apiUrl("facebookPartnerValidateInvite")}?inviteToken=${encodeURIComponent(inviteToken)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "This invite is not valid anymore.");
      }

      setInvite(data.invite || null);
    } catch (error) {
      setInviteError(error instanceof Error ? error.message : "This invite is not valid anymore.");
    } finally {
      setIsLoadingInvite(false);
    }
  }, [inviteToken, isOauthReturn]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  const handleStartConnect = async () => {
    if (!inviteToken) return;

    setIsStartingAuth(true);
    setPartnerMessage("");

    try {
      const response = await fetch(apiUrl("facebookPartnerStartAuth"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inviteToken,
          redirectOrigin: window.location.origin,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to start Facebook authorization.");
      }

      window.location.href = data.authUrl;
    } catch (error) {
      setPartnerMessage(error instanceof Error ? error.message : "Failed to start Facebook authorization.");
      setIsStartingAuth(false);
    }
  };

  const handleFacebookCallback = useCallback(async () => {
    if (!oauthCode || !oauthState || handledOauthRef.current) return;

    handledOauthRef.current = true;
    setIsHandlingCallback(true);
    setPartnerMessage("Loading your Facebook Pages...");

    try {
      const response = await fetch(apiUrl("facebookPartnerHandleCallback"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: oauthCode,
          state: oauthState,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to complete Facebook authorization.");
      }

      const pages = Array.isArray(data.pages) ? data.pages : [];
      let pagesWithStatuses = pages;

      if (pages.length) {
        try {
          const statusesResponse = await fetch(apiUrl("facebookPartnerGetPageStatuses"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              state: data.state || oauthState,
              pageIds: pages.map((page: CandidatePage) => page.pageId),
            }),
          });
          const statusesData = await statusesResponse.json();

          if (statusesResponse.ok && Array.isArray(statusesData.statuses)) {
            pagesWithStatuses = mergeCandidatePageStatuses(pages, statusesData.statuses);
          }
        } catch {
          // Keep the original page list if the status refresh fails.
        }
      }

      setInvite(data.invite || null);
      setCandidatePages(pagesWithStatuses);
      setCallbackState(data.state || oauthState);
      setPartnerMessage(
        pages.length
          ? "Choose the Facebook Page you want to submit for Bacolod AI approval."
          : "Facebook authorized, but no Pages were returned for this account."
      );
      clearOauthParams();
    } catch (error) {
      setPartnerMessage(error instanceof Error ? error.message : "Failed to complete Facebook authorization.");
    } finally {
      setIsHandlingCallback(false);
    }
  }, [clearOauthParams, oauthCode, oauthState]);

  useEffect(() => {
    handleFacebookCallback();
  }, [handleFacebookCallback]);

  const handleSubmitPage = async (pageId: string) => {
    if (!callbackState) {
      setPartnerMessage("Your authorization session is missing. Please connect again.");
      return;
    }

    setSubmittingPageId(pageId);
    setPartnerMessage("Submitting your Page for approval...");

    try {
      const response = await fetch(apiUrl("facebookPartnerSubmitPage"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          state: callbackState,
          pageId,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit your Page.");
      }

      setCandidatePages((currentPages) =>
        currentPages.map((page) =>
          page.pageId === pageId
            ? {
                ...page,
                approvalStatus: data.approvalStatus === "approved" ? "approved" : "pending",
              }
            : page
        )
      );
      setPartnerMessage(data.message || "Your Page is now pending approval.");
    } catch (error) {
      setPartnerMessage(error instanceof Error ? error.message : "Failed to submit your Page.");
    } finally {
      setSubmittingPageId(null);
    }
  };

  const getPageButtonLabel = (page: CandidatePage) => {
    if (submittingPageId === page.pageId) return "Submitting...";
    if (page.approvalStatus === "approved") return "Already approved";
    if (page.approvalStatus === "pending") return "Already submitted";
    return "Submit for approval";
  };

  const isPageButtonDisabled = (page: CandidatePage) =>
    Boolean(submittingPageId) || page.approvalStatus === "approved" || page.approvalStatus === "pending";

  const showCallbackLoading =
    isOauthReturn &&
    !inviteError &&
    !candidatePages.length &&
    !callbackState &&
    (isHandlingCallback || !partnerMessage);

  return (
    <IonPage className="city-updates-page">
      <IonHeader className="class-header">
        <IonToolbar className="city-updates-header" />
      </IonHeader>

      <IonContent fullscreen className="city-updates-content web-centered">
        <div className="city-updates-shell">
          <div className="city-updates-hero city-updates-hero-wide">
            <div className="city-updates-card city-updates-page-card updates-portal-card updates-connect-card">
              <div className="updates-live-hero">
                <div className="updates-connect-topmark" aria-hidden="true">
                  <div className="updates-connect-logo-stage">
                    <div className="updates-connect-logo-orbit updates-connect-logo-orbit-a" />
                    <div className="updates-connect-logo-orbit updates-connect-logo-orbit-b" />
                    <div className="updates-connect-logo-shell">
                      <img src="/assets/icon/icon.png" alt="Bacolod AI" className="updates-connect-logo-image" />
                    </div>
                    <div className="updates-connect-logo-glow" />
                  </div>
                </div>
                <div className="updates-live-pill">
                  <IonIcon icon={logoFacebook} />
                  <span>Partner connect</span>
                </div>
                <h2 className="updates-live-title">Connect your Facebook Page to Bacolod City AI Guide</h2>
                <p className="updates-live-copy">
                  Connect the official Page you manage, and we&apos;ll review it before its posts sync into Bacolod AI&apos;s database.
                </p>
                <div className="updates-connect-showcase">
                  <div className="updates-connect-scene-card">
                    <img src={bgFestival} alt="Bacolod festival lights" className="updates-connect-scene-image" />
                    <div className="updates-connect-scene-links">
                      <a href="https://ai.explorebacolod.com/" target="_blank" rel="noreferrer" className="updates-connect-link-pill">
                        <IonIcon icon={globeOutline} />
                        <span>Bacolod AI website</span>
                      </a>
                      <a
                        href="https://apps.apple.com/ph/app/bacolod-city-ai-guide/id6751321206"
                        target="_blank"
                        rel="noreferrer"
                        className="updates-connect-link-pill"
                      >
                        <IonIcon icon={logoApple} />
                        <span>App Store</span>
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.bldapps.bacolodai.md"
                        target="_blank"
                        rel="noreferrer"
                        className="updates-connect-link-pill"
                      >
                        <IonIcon icon={logoGooglePlaystore} />
                        <span>Google Play</span>
                      </a>
                    </div>
                    <div className="updates-connect-scene-copy">
                      <strong>Built for trusted Bacolod city updates</strong>
                    </div>
                  </div>
                </div>
              </div>

              {isLoadingInvite || showCallbackLoading ? (
                <div className="updates-loading-state">{showCallbackLoading ? "Loading your Facebook Pages..." : "Checking invite link..."}</div>
              ) : inviteError ? (
                <div className="updates-error">{inviteError}</div>
              ) : (
                <>
                  {invite && (
                    <div className="updates-admin-panel updates-partner-panel">
                      <div className="updates-admin-head">
                        <div>
                          <span className="updates-admin-kicker">Invitation to</span>
                          <h3>{invite.label}</h3>
                        </div>
                        <div className="updates-admin-lock">
                          <span>Link valid until {formatExpiry(invite.expiresAtMs)}</span>
                        </div>
                      </div>

                      <p className="updates-partner-note">
                        This invite is meant for the person or team managing the Facebook Page.
                      </p>

                      {!candidatePages.length && (
                        <div className="updates-connect-actions">
                          <button
                            type="button"
                            className="updates-admin-button updates-admin-button-primary updates-facebook-button"
                            onClick={handleStartConnect}
                            disabled={isStartingAuth || isHandlingCallback || Boolean(submittingPageId)}
                          >
                            <IonIcon icon={logoFacebook} />
                            <span>{isStartingAuth ? "Opening Facebook..." : "Connect with Facebook"}</span>
                          </button>
                        </div>
                      )}

                      {partnerMessage && <div className="updates-admin-message">{partnerMessage}</div>}

                      {candidatePages.length > 0 && (
                        <div className="updates-admin-section">
                          <div className="updates-admin-section-head">
                            <span>Choose your Page</span>
                            <small>We&apos;ll review it first before it is added to Bacolod AI&apos;s database.</small>
                          </div>

                          <div className="updates-admin-grid">
                            {candidatePages.map((page) => (
                              <div key={page.pageId} className="updates-source-card updates-source-card-selectable">
                                <div className="updates-source-main">
                                  {page.pictureUrl ? <img src={page.pictureUrl} alt={page.pageName} /> : <div className="updates-source-fallback">FB</div>}
                                  <div>
                                    <strong>{page.pageName}</strong>
                                    <span>{page.category || "Facebook Page"}</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="updates-admin-button updates-admin-button-primary"
                                  onClick={() => handleSubmitPage(page.pageId)}
                                  disabled={isPageButtonDisabled(page)}
                                >
                                  <IonIcon icon={checkmarkCircleOutline} />
                                  <span>{getPageButtonLabel(page)}</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default UpdatesConnect;
